import { ListItem, ListItemText, IconButton, Box, Typography } from '@mui/material'
import {
  Close,
  NotificationsActive,
  Warning,
  EmojiEvents,
  Inventory,
  CalendarToday,
} from '@mui/icons-material'
import { useNotificationStore } from '@/store/useNotificationStore'
import type { Notification } from '@/types/notifications'
import { formatDistanceToNow } from 'date-fns'

interface NotificationItemProps {
  notification: Notification
}

const notificationIcons = {
  task_reminder: NotificationsActive,
  task_overdue: Warning,
  achievement: EmojiEvents,
  low_stock: Inventory,
  daily_summary: CalendarToday,
}

const notificationColors = {
  task_reminder: '#6366f1',
  task_overdue: '#f59e0b',
  achievement: '#ffd700',
  low_stock: '#ef4444',
  daily_summary: '#10b981',
}

export default function NotificationItem({ notification }: NotificationItemProps) {
  const { markAsRead, deleteNotification } = useNotificationStore()

  const Icon = notificationIcons[notification.type] || NotificationsActive
  const color = notificationColors[notification.type] || '#6366f1'

  const handleClick = async () => {
    if (!notification.read) {
      await markAsRead(notification.id)
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await deleteNotification(notification.id)
  }

  return (
    <ListItem
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        bgcolor: notification.read ? 'transparent' : 'rgba(99, 102, 241, 0.05)',
        '&:hover': {
          bgcolor: notification.read ? 'action.hover' : 'rgba(99, 102, 241, 0.1)',
        },
        transition: 'background-color 0.2s',
        px: 2,
        py: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
        }}
      >
        <Icon sx={{ color, fontSize: 24 }} />
      </Box>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.read ? 500 : 700,
                flex: 1,
              }}
            >
              {notification.title}
            </Typography>
            {!notification.read && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
            )}
          </Box>
        }
        secondary={
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 0.5 }}
            >
              {notification.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </Typography>
          </>
        }
      />

      <IconButton
        size="small"
        onClick={handleDelete}
        sx={{
          ml: 1,
          '&:hover': {
            color: 'error.main',
          },
        }}
      >
        <Close fontSize="small" />
      </IconButton>
    </ListItem>
  )
}
